import { ActionTree } from 'vuex'
import { RootState } from '../types'
import { Api } from '@/api/Api'

import State, { IChangePasswordRequest, ILoginRequest, IPasswordResetRequest, ISignupRequest, IUpdatePasswordWithCodeRequest, User } from './types'
import { HttpMethod } from '@/api/FetchApi'
import VueJwtDecode from 'vue-jwt-decode'
import { isAuthenticated } from '@/common/helpers/user-helpers'
import { isCustomerSite } from '@/helpers/sites.helper'
import SecurityState from '../security/types'

const api = new Api()

export const actions: ActionTree<State, RootState> = {
  getCurrentUser({ commit, state }): any {
    if (!isAuthenticated(state.currentUser)) {
      const url = '/Security/GetCurrentUser'
      return api.fetch({
        url,
        user: state.currentUser,
      })
        .then(response => {
          const user: User = response
          commit('currentUserLoaded', { user })
          return user
        }).catch(() => {
          // commit('currentUserError')
        })
    }

    const url = '/User/me'
    return api.fetchV2({
      url,
      user: state.currentUser,
    })
      .then(response => {
        if (!response.Successful) {
          throw response
        }

        const decodedUser = VueJwtDecode.decode(state.currentUser.BearerToken)

        const user: User = {
          Id: decodedUser['user-id'],
          Name: response.Name,
          LastName: response.LastName,
          Username: response.Username,
          BearerToken: state.currentUser.BearerToken,
          TokenExpiration: state.currentUser.TokenExpiration,
          IsCodeToken: response.IsCodeToken,
          // TODO jm
          HasAccess: true,
          EffectivePermissions: decodedUser.modulePermissions ? JSON.parse(decodedUser.modulePermissions) : [],
          Role: state.currentUser.Role,
          ResourcePermissions: decodedUser.permissions
            ? (Array.isArray(decodedUser.permissions) ? decodedUser.permissions : [decodedUser.permissions]).map((item: string) => JSON.parse(item))
            : [],
          AuthProviderType: decodedUser.authProviderType,
          Email: decodedUser.mail,
          AcceptanceDone: decodedUser.acceptanceDone,
        }

        commit('currentUserLoaded', { user })
        return user
      }).catch(() => {
        commit('currentUserError')
      })
  },
  login({ commit, rootState }, { loginRequest }: { loginRequest: ILoginRequest }): any {
    commit('userPasswordReset')
    commit('currentUserLoading')
    const url = '/Authentication/Login'
    const obj = loginRequest
    const siteName = rootState.site.Name
    obj.Scope = isCustomerSite(siteName) ? 'customer' : 'sales-rep'
    return api.fetchV2({
      url,
      obj,
      method: HttpMethod.Post,
    }).then(response => {
      if (!response.Successful) {
        if (response.CodeString === 'PasswordExpired') {
          commit('userPasswordExpired', obj.Username)
        }
        throw response
      }

      const decodedUser = VueJwtDecode.decode(response.Token)
      const user: User = {
        Id: decodedUser['user-id'],
        Name: decodedUser.name,
        LastName: decodedUser.lastName,
        Username: decodedUser.email,
        BearerToken: response.Token,
        IsCodeToken: response.IsCodeToken,
        TokenExpiration: decodedUser.exp,
        // TODO jm
        Role: response.Role,
        HasAccess: true,
        EffectivePermissions: decodedUser.modulePermissions ? JSON.parse(decodedUser.modulePermissions) : [],
        ResourcePermissions: decodedUser.permissions
          ? (Array.isArray(decodedUser.permissions) ? decodedUser.permissions : [decodedUser.permissions]).map((item: string) => JSON.parse(item))
          : [],
        AuthProviderType: decodedUser.authProviderType,
        Email: decodedUser.mail,
        AcceptanceDone: decodedUser.acceptanceDone,
      }
      if (decodedUser.acceptanceDone && response.IsCodeToken === false) {
        commit('currentUserLoaded', { user })
        return user
      } else if (response.IsCodeToken) {
        commit('loginCodeUserLoaded', { user })
        return user
      } else {
        commit('acceptanceUserLoaded', { user })
        return user
      }
    },
    () => commit('currentUserError'))
  },
  checkCode({ commit }, { code }) {
    const url = '/Authentication/checkCode'
    const securityState = (this.state as any).security as SecurityState

    return api.fetchV2({
      url,
      obj: code,
      method: HttpMethod.Post,
      user: securityState.loginCodeUser,
    }).then(response => {
      if (!response.Successful) {
        throw response
      } else {
        const decodedUser = VueJwtDecode.decode(response.Token)
        const user: User = {
          Id: decodedUser['user-id'],
          Name: decodedUser.name,
          LastName: decodedUser.lastName,
          Username: decodedUser.email,
          BearerToken: response.Token,
          IsCodeToken: response.IsCodeToken,
          TokenExpiration: decodedUser.exp,
          // TODO jm
          Role: response.Role,
          HasAccess: true,
          EffectivePermissions: decodedUser.modulePermissions ? JSON.parse(decodedUser.modulePermissions) : [],
          ResourcePermissions: decodedUser.permissions
            ? (Array.isArray(decodedUser.permissions) ? decodedUser.permissions : [decodedUser.permissions]).map((item: string) => JSON.parse(item))
            : [],
          AuthProviderType: decodedUser.authProviderType,
          Email: decodedUser.mail,
          AcceptanceDone: decodedUser.acceptanceDone,
        }

        if (decodedUser.acceptanceDone) {
          commit('currentUserLoaded', { user })
        } else {
          commit('acceptanceUserLoaded', { user })
        }
        return user
      }
    })
  },
  resendEmail() {
    const url = '/Authentication/resendEmail'
    const securityState = (this.state as any).security as SecurityState

    return api.fetchV2({
      url,
      method: HttpMethod.Post,
      user: securityState.acceptanceUser,
    }).then(response => {
      return response
    })
  },
  logout({ commit, state }): any {
    const url = '/Authentication/Logout'
    return api.fetchV2({
      url,
      method: HttpMethod.Post,
      user: state.currentUser,
    }).finally(() => commit('currentUserLoggedOut'))
  },
  signup({ commit, rootState, state }, { signupRequest }: { signupRequest: ISignupRequest }): any {
    const url = state.currentUser?.AuthProviderType === 'AD' ? '/Authentication/AdSignup' : '/Authentication/Signup'
    const obj = signupRequest
    if (state.currentUser?.AuthProviderType !== 'AD') {
      commit('currentUserLoading')
    }

    const siteName = rootState.site.Name
    obj.Scope = isCustomerSite(siteName) ? 'customer' : 'sales-rep'
    return api.fetchV2({
      url,
      obj,
      method: HttpMethod.Post,
      user: state.currentUser,
    }).then(response => {
      if (!response.Successful) {
        throw response
      }

      const decodedUser = VueJwtDecode.decode(response.Token)
      const user: User = {
        Id: decodedUser['user-id'],
        Name: decodedUser.name,
        LastName: decodedUser.lastName,
        Username: decodedUser.email,
        BearerToken: response.Token,
        IsCodeToken: response.IsCodeToken,
        Role: decodedUser.Role,
        // TODO jm
        HasAccess: true,
        EffectivePermissions: [],
        ResourcePermissions: [],
        AuthProviderType: decodedUser.authProviderType,
        TokenExpiration: decodedUser.exp,
        Email: decodedUser.mail,
        AcceptanceDone: decodedUser.acceptanceDone,
      }
      commit('currentUserLoaded', { user })
      return user
    })
  },
  resetpassword(store, { passwordResetRequest }: { passwordResetRequest: IPasswordResetRequest }): any {
    const url = '/Account/ResetPassword'
    const obj = passwordResetRequest
    return api.fetchV2({
      url,
      obj,
      method: HttpMethod.Post,
    }).then(response => {
      if (!response.Successful) {
        throw response
      }
    })
  },
  updatepassword(store, { resetPasswordRequest }: { resetPasswordRequest: IUpdatePasswordWithCodeRequest }): any {
    const url = '/Account/UpdatePassword'
    const obj = resetPasswordRequest
    return api.fetchV2({
      url,
      obj,
      method: HttpMethod.Post,
    }).then(response => {
      if (!response.Successful) {
        throw response
      }
    })
  },
  changePassword({ commit, state }, { changePasswordRequest }: { changePasswordRequest: IChangePasswordRequest }): any {
    const url = '/Account/ChangePassword'
    const obj = changePasswordRequest
    return api.fetchV2({
      url,
      user: state.currentUser,
      obj,
      method: HttpMethod.Post,
    }).then(response => {
      if (!response.Successful) {
        throw response
      }
      commit('userPasswordReset')
    })
  },
  getAcceptanceDocuments({ commit }): any {
    const url = '/Authentication/AcceptanceDocuments'
    return api.fetchV2({
      url,
    }).then(response => {
      return response
    },
    () => commit('currentUserError'))
  },
  saveUserAcceptance({ commit, state }, { siteDocumentIds, accepted }: { siteDocumentIds: number[], accepted: boolean }): any {
    const url = '/Authentication/AcceptanceDocuments'
    const obj: any = []
    siteDocumentIds.forEach(s => obj.push({ SiteDocumentId: s, Accepted: accepted }))
    return api.fetchV2({
      url,
      obj,
      method: HttpMethod.Post,
      user: state.acceptanceUser,
    }).then(response => {
      if (response.Accepted) {
        commit('acceptanceSuccess')
      } else {
        commit('acceptanceDone')
      }
      return response.Accepted
    },
    () => commit('currentUserError'))
  },
}
